import { faFolderPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@kcftech/design-system';
import {
    ICorporationResponse,
    ILocationResponse,
    IPictureFileMetadataResponse,
} from '@kcftech/prometheustypescriptapiclient';
import { DebouncedSearchInput } from 'components/data/DebouncedSearchInput';
import { DocumentTitle } from 'components/DocumentTitle';
import { TablePageLayout } from 'components/page/styled';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { MultiFileUpload } from 'components/upload/MultiFileUpload';
import { usePermissions } from 'contexts/PermissionsContext';
import { usePagedFileLibraryDataSource } from 'custom_hooks/data_hooks/data_source/fileLibraryDataSource';
import { useRootFolder } from 'custom_hooks/data_hooks/file/useRootFolder';
import { FileBreadcrumb } from 'pages/Settings/FileLibrary/FileBreadcrumb';
import { FileDetailsPage } from 'pages/Settings/FileLibrary/FileDetails/FileDetailsPage';
import { FileLibraryTable } from 'pages/Settings/FileLibrary/FileLibraryTable';
import { FolderModal } from 'pages/Settings/FileLibrary/Folder/FolderModal';
import { SearchCreateButtonContainer } from 'pages/Settings/shared/styled';
import { useEffect, useState } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import {
    ProtectedCorporationContent,
    ProtectedLocationContent,
} from 'routing/EntityContent';
import { PremiumContent } from 'routing/PremiumContent';

export type FileLibraryProps = Readonly<{
    corporation?: ICorporationResponse;
    location?: ILocationResponse;
}>;

export function FileLibraryPage({ location, corporation }: FileLibraryProps) {
    const { canUploadToFileLibrary } = usePermissions();
    const [nameSearch, setNameSearch] = useState('');
    const [parentFolderId, setParentFolderId] = useState<string | undefined>();
    const [foldersHierarchy, setFoldersHierarchy] = useState<
        IPictureFileMetadataResponse[]
    >([]);
    const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);

    const { url } = useRouteMatch();

    const corporationId = corporation?.Id;
    const locationId = location?.Id;

    const { data: rootFolder, isLoading: isRootFolderLoading } = useRootFolder({
        corporationId,
        locationId,
    });

    const dataSource = usePagedFileLibraryDataSource({
        name: 'file-library-table',
        defaultFilter: {
            nameSearch,
            corporationId,
            locationId,
            parentFolderId: rootFolder?.Id,
        },
    });
    const { setFilter } = dataSource;

    useEffect(() => {
        setParentFolderId(rootFolder?.Id);
    }, [rootFolder?.Id]);

    useEffect(() => {
        setFilter((current) => ({
            ...current,
            corporationId,
            locationId,
            nameSearch,
            parentFolderId,
        }));
    }, [setFilter, corporationId, locationId, nameSearch, parentFolderId]);

    const onFolderClick = (folderData: IPictureFileMetadataResponse) => {
        setFoldersHierarchy((current) => [...current, folderData]);
        setParentFolderId(folderData.Id);
        setNameSearch('');
    };

    const onBreadcrumbClick = (folderData: IPictureFileMetadataResponse) => {
        setParentFolderId(folderData.Id);
        if (folderData.Id === rootFolder?.Id) {
            // if clicking the root ("File Library" in the breadcrumb) remove all other items
            setFoldersHierarchy([]);
        } else {
            // remove all folders in the breadcrumb after the index of the selected folder
            setFoldersHierarchy((current) => {
                const folderIndex = current.indexOf(folderData);
                return current.slice(0, folderIndex + 1);
            });
        }
    };

    return (
        <Switch>
            <Route
                path={`${url}/:fileId`}
                exact
                render={() => (
                    <PremiumContent allowSaas>
                        <ProtectedCorporationContent
                            pageName="corporation-file-details"
                            render={() => (
                                <FileDetailsPage refreshTable={dataSource.reloadPage} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            <Route
                path={`${url}/:fileId`}
                exact
                render={() => (
                    <PremiumContent allowSaas>
                        <ProtectedLocationContent
                            pageName="location-file-details"
                            render={() => (
                                <FileDetailsPage refreshTable={dataSource.reloadPage} />
                            )}
                        />
                    </PremiumContent>
                )}
            />
            {/* @ts-expect-error: type issue with older version of react-router-dom */}
            <Route default>
                <TablePageLayout>
                    <DocumentTitle title={`${corporation?.Name ?? ''} File Library`} />
                    <SearchCreateButtonContainer>
                        <DebouncedSearchInput
                            placeholder="Search"
                            value={nameSearch}
                            onChange={setNameSearch}
                        />
                        {canUploadToFileLibrary && (
                            <div>
                                <Button
                                    className="mr-10"
                                    title="Add Folder"
                                    appearance="primary"
                                    spacing="medium"
                                    icon={<FontAwesomeIcon icon={faFolderPlus} />}
                                    onClick={() => setAddFolderModalOpen(true)}
                                >
                                    Add Folder
                                </Button>
                                <MultiFileUpload
                                    location={location}
                                    corporation={corporation}
                                    parentFolderId={parentFolderId}
                                    onUpload={() => dataSource.reloadPage()}
                                />
                            </div>
                        )}
                    </SearchCreateButtonContainer>
                    <SpinnerArea isLoading={isRootFolderLoading} hideChildren noContainer>
                        {rootFolder && (
                            <>
                                <FileBreadcrumb
                                    onBreadcrumbClick={onBreadcrumbClick}
                                    rootFolder={rootFolder}
                                    foldersHierarchy={foldersHierarchy}
                                />
                                <FileLibraryTable
                                    dataSource={dataSource}
                                    foldersHierarchy={foldersHierarchy}
                                    locationId={locationId}
                                    onFolderClick={onFolderClick}
                                    corporationId={corporationId}
                                />
                            </>
                        )}
                        {addFolderModalOpen && (
                            <FolderModal
                                locationId={locationId}
                                corporationId={corporationId}
                                parentFolderId={parentFolderId ?? rootFolder?.Id}
                                onCancel={() => setAddFolderModalOpen(false)}
                                onSave={() => {
                                    dataSource.reloadPage();
                                    setAddFolderModalOpen(false);
                                }}
                            />
                        )}
                    </SpinnerArea>
                </TablePageLayout>
            </Route>
        </Switch>
    );
}
